import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isArchived } from '../config/archivedPages';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isOurCareDropdownOpen, setIsOurCareDropdownOpen] = useState(false);
  const [isOurServicesDropdownOpen, setIsOurServicesDropdownOpen] = useState(false);
  const [isOurEthosDropdownOpen, setIsOurEthosDropdownOpen] = useState(false);
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    setIsOurCareDropdownOpen(false);
    setIsOurServicesDropdownOpen(false);
    setIsOurEthosDropdownOpen(false);
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const nav = document.querySelector('.header__nav');
      const hamburger = document.querySelector('.hamburger');
      if (isMenuOpen && nav && !nav.contains(event.target as Node) && hamburger && !hamburger.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleOurCareDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOurCareDropdownOpen(!isOurCareDropdownOpen);
  };

  const toggleOurServicesDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOurServicesDropdownOpen(!isOurServicesDropdownOpen);
  };

  const toggleOurEthosDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOurEthosDropdownOpen(!isOurEthosDropdownOpen);
  };

  return (
    <header className="header">
      <div className="header__plate" aria-hidden="true">
        <svg
          className="header__plate-svg"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1920 64"
          preserveAspectRatio="none"
        >
          <path
            fill="var(--brand-light)"
            d="M0,0 L1920,0 L1920,48 C1600,52 1280,50 960,54 C640,52 320,50 0,48 L0,0 Z"
          />
          <path
            fill="var(--brand-dark)"
            d="M0,0 L1920,0 L1920,48 C1600,50 1280,49 960,52 C640,50 320,49 0,48 L0,0 Z"
          />
        </svg>
      </div>
      <div className="header__container">
        <div className="header__logo">
          <Link
            to="/"
            className="header__wordmark-link"
            aria-current={location.pathname === '/' ? 'page' : undefined}
          >
            <span className="header__wordmark-stack">
              <span className="header__wordmark-title">Graham Care</span>
              <span className="header__wordmark-subtitle">Care Suites</span>
            </span>
          </Link>
        </div>
        
        <button 
          className={`hamburger ${isMenuOpen ? 'is-active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <span className="hamburger__line"></span>
          <span className="hamburger__line"></span>
          <span className="hamburger__line"></span>
        </button>

        <nav className={`header__nav ${isMenuOpen ? 'is-open' : ''}`}>
          <ul className="nav__list">
            <li><Link to="/">Home</Link></li>
            <li className={`has-dropdown ${isDropdownOpen ? 'dropdown-open' : ''}`}>
              <a href="#about" onClick={toggleDropdown}>
                About Us <span className="dropdown-arrow">▼</span>
              </a>
              <ul className="dropdown">
                <li
                  className={`has-nested-dropdown ${isOurCareDropdownOpen ? 'nested-dropdown-open' : ''}`}
                  onMouseLeave={() => setIsOurCareDropdownOpen(false)}
                >
                  <a
                    href="#our-care"
                    onClick={toggleOurCareDropdown}
                    onMouseEnter={() => setIsOurCareDropdownOpen(true)}
                  >
                    Our Care <span className="dropdown-arrow">▶</span>
                  </a>
                  <ul className="nested-dropdown">
                    {!isArchived('/care-suites') && <li><Link to="/care-suites">Care Suites</Link></li>}
                    {!isArchived('/activities') && <li><Link to="/activities">Activities</Link></li>}
                    {!isArchived('/wellbeing-centred-care') && <li><Link to="/wellbeing-centred-care">Wellbeing Centred Care</Link></li>}
                    {!isArchived('/testimonials') && <li><Link to="/testimonials">Testimonials</Link></li>}
                    {!isArchived('/meet-the-team') && <li><Link to="/meet-the-team">Meet the Team</Link></li>}
                    {!isArchived('/weekly-menus') && <li><Link to="/weekly-menus">Weekly Menus</Link></li>}
                    {!isArchived('/photo-gallery') && <li><Link to="/photo-gallery">Photo Gallery</Link></li>}
                  </ul>
                </li>
                <li className={`has-nested-dropdown ${isOurServicesDropdownOpen ? 'nested-dropdown-open' : ''}`}>
                  <a 
                    href="#our-services" 
                    onClick={toggleOurServicesDropdown}
                    onMouseEnter={() => setIsOurServicesDropdownOpen(true)}
                    onMouseLeave={() => setIsOurServicesDropdownOpen(false)}
                  >
                    Our Services <span className="dropdown-arrow">▶</span>
                  </a>
                  <ul className="nested-dropdown">
                    {!isArchived('/respite-care') && <li><Link to="/respite-care">Respite Care</Link></li>}
                    {!isArchived('/dementia-care') && <li><Link to="/dementia-care">Dementia Care</Link></li>}
                    {!isArchived('/nursing-care') && <li><Link to="/nursing-care">Nursing Care</Link></li>}
                    {!isArchived('/rehab') && <li><Link to="/rehabilitation">Rehabilitation</Link></li>}
                  </ul>
                </li>
                <li className={`has-nested-dropdown ${isOurEthosDropdownOpen ? 'nested-dropdown-open' : ''}`}>
                  <a 
                    href="#our-ethos" 
                    onClick={toggleOurEthosDropdown}
                    onMouseEnter={() => setIsOurEthosDropdownOpen(true)}
                    onMouseLeave={() => setIsOurEthosDropdownOpen(false)}
                  >
                    Our Ethos <span className="dropdown-arrow">▶</span>
                  </a>
                  <ul className="nested-dropdown">
                    {!isArchived('/servant-leadership') && <li><Link to="/servant-leadership">Servant Leadership</Link></li>}
                    {!isArchived('/quality-assurance') && <li><Link to="/quality-assurance">Quality Assurance</Link></li>}
                    {!isArchived('/governance') && <li><Link to="/governance">Governance</Link></li>}
                    {!isArchived('/gmb-union') && <li><Link to="/gmb-union">GMB Union</Link></li>}
                  </ul>
                </li>
                {!isArchived('/help-and-advice') && <li><Link to="/help-and-advice">Help and Advice</Link></li>}
                {!isArchived('/cqc-registration') && <li><Link to="/cqc-registration">CQC Registration</Link></li>}
              </ul>
            </li>
            <li><Link to="/our-homes">Our Homes</Link></li>
            <li><Link to="/developments">Developments</Link></li>
            {!isArchived('/digital-transformation') && (
              <li>
                <Link to="/digital-transformation">Digital Transformation</Link>
              </li>
            )}
            {!isArchived('/work-with-us') && <li><Link to="/work-with-us">Work with Us</Link></li>}
            {!isArchived('/contact-us') && <li><Link to="/contact-us">Contact Us</Link></li>}
          </ul>
        </nav>
      </div>
    </header>
  );
}

export default Header;

